import { sbEditable } from '@storyblok/storyblok-editable';
import classNames from 'classnames';
import type { ReactElement, RefObject } from 'react';
import { useRef } from 'react';
import type { VideoBlockProps } from './VideoBlock.data';
import styles from './VideoBlock.module.scss';
import Video from '../../atoms/video/Video';
import { MediaLayout } from '../../utils/media-layout/MediaLayout';
import useThemeScrollBlock from '../../../hooks/useThemeScrollBlock';
import { cleanMediaQueries } from '../../../hooks/useDeviceStateTracker';
import { isDesktop } from '../../../hooks/useIsDesktopDevice';
import useScrollTrigger from '../../../hooks/useScrollTrigger';

export default function VideoBlock({
  className,
  blok,
  canTransitionIn,
  ref,
}: VideoBlockProps): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;

  let imageSizes = `${cleanMediaQueries.MIN_768} 768px, ${cleanMediaQueries.MIN_1920} 1024px, 100vw`;
  if (blok.layout_size !== 'small') {
    imageSizes = '100vw';
  }

  const { inView } = useScrollTrigger({
    trigger: elementRef,
    canTransitionIn,
    enableInView: true,
  });

  useThemeScrollBlock(elementRef, blok.use_accent_color);

  return (
    <MediaLayout
      ref={elementRef}
      className={classNames(styles.videoBlock, className, {
        [styles.noMargins]: blok.no_margins,
      })}
      blok={blok}
      {...sbEditable(blok)}
    >
      <Video
        openInModal={blok.is_background ? false : !isDesktop}
        pauseWhenOutViewport
        imageSizes={imageSizes}
        playing={blok.is_background && inView}
        blok={blok.video[0]}
        objectFit="contain"
        controls={!blok.is_background}
        innerTransition={{
          type: 'scaleUp',
        }}
        loop={blok.is_background}
        playButton={!blok.is_background}
        muted={blok.is_background}
      />
    </MediaLayout>
  );
}
